/**
 * V2 Theme Settings Import/Export Utilities
 *
 * Pure utility functions for exporting and importing OnboardingThemeSettingsV2
 * as JSON files. Only V2-native theme files are accepted.
 *
 * Uses __segmently_theme_v2__ marker (parallel to V1's __segmently_theme__).
 */

import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';

// ============================================================================
// Types
// ============================================================================

/**
 * JSON envelope for V2 theme settings export.
 */
export interface ThemeSettingsV2ExportEnvelope {
  /** V2 marker for identification — always true */
  __segmently_theme_v2__: true;
  /** Schema version */
  version: '2.0.0';
  /** Exported V2 theme settings data */
  data: OnboardingThemeSettingsV2;
  /** Export metadata (informational only) */
  metadata: {
    projectId: string;
    onboardingId: string;
    exportedAt: string;
  };
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Type guard: checks if parsed JSON is a valid V2 ThemeSettingsExportEnvelope.
 * Validates the V2 marker and required top-level data fields.
 */
export function isThemeSettingsV2Envelope(data: unknown): data is ThemeSettingsV2ExportEnvelope {
  if (typeof data !== 'object' || data === null) return false;

  const obj = data as Record<string, unknown>;
  if (obj.__segmently_theme_v2__ !== true) return false;

  const inner = obj.data;
  if (typeof inner !== 'object' || inner === null) return false;

  const settings = inner as Record<string, unknown>;
  return (
    typeof settings.colors === 'object' && settings.colors !== null &&
    typeof settings.fonts === 'object' && settings.fonts !== null &&
    typeof settings.optionsListSettings === 'object' && settings.optionsListSettings !== null &&
    typeof settings.background === 'object' && settings.background !== null
  );
}

// ============================================================================
// Export
// ============================================================================

/**
 * Build a V2 ThemeSettingsExportEnvelope from OnboardingThemeSettingsV2.
 * Deep-clones the settings to avoid mutation.
 */
export function buildThemeV2ExportData(
  themeSettings: OnboardingThemeSettingsV2,
  projectId: string,
  onboardingId: string,
): ThemeSettingsV2ExportEnvelope {
  const cloned = typeof structuredClone === 'function'
    ? structuredClone(themeSettings)
    : JSON.parse(JSON.stringify(themeSettings)) as OnboardingThemeSettingsV2;

  return {
    __segmently_theme_v2__: true,
    version: '2.0.0',
    data: cloned,
    metadata: {
      projectId,
      onboardingId,
      exportedAt: new Date().toISOString(),
    },
  };
}

/**
 * Trigger a browser download of the V2 theme settings JSON file.
 */
export function downloadThemeV2JSON(
  envelope: ThemeSettingsV2ExportEnvelope,
  onboardingName?: string,
): void {
  const blob = new Blob(
    [JSON.stringify(envelope, null, 2)],
    { type: 'application/json' },
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const cleanName = (onboardingName || 'theme-v2')
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  link.download = `theme_v2_${cleanName}_${timestamp}.json`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================================
// Import
// ============================================================================

/**
 * Parse and validate a V2-native theme settings JSON file.
 *
 * @throws Error if JSON is invalid or doesn't match V2 envelope format
 */
export function parseThemeV2ImportFile(content: string): OnboardingThemeSettingsV2 {
  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    throw new Error('Invalid JSON file');
  }

  if (!isThemeSettingsV2Envelope(parsed)) {
    throw new Error(
      'Invalid V2 theme file: missing __segmently_theme_v2__ marker or required fields (colors, fonts, optionsListSettings, background)',
    );
  }

  return parsed.data;
}

/**
 * Parse a theme settings JSON file as V2.
 * Accepts V2-native format only. Legacy V1 theme conversion is intentionally
 * retired from active UI import paths.
 *
 * @throws Error if content is not valid V2 theme JSON
 */
export function parseThemeImportFileAsV2(content: string): OnboardingThemeSettingsV2 {
  return parseThemeV2ImportFile(content);
}
